/**
 * AI Planning Page
 * Scenario overview with status cards, scenario list, and actions
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import {
  usePlanningScenarios,
  usePlanningScenarioSummary,
  useCalculateScenario,
  useApproveScenario,
  useRejectScenario,
  useCreateScenario,
  useDeleteScenario,
  getScenarioStatusInfo,
} from '../hooks/usePlanningScenarios';
import type { PlanningScenario, ScenarioStatus, CreateScenarioPayload } from '../hooks/usePlanningScenarios';
import { Modal } from '../components/ui/Modal';
import {
  Brain,
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  RefreshCw,
  Loader2,
  ChevronRight,
  Zap,
  BarChart3,
  Target,
  Plus,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';

// Status card config
const statusCards: { key: ScenarioStatus; label: string; icon: React.ElementType; color: string; bg: string }[] = [
  { key: 'draft', label: 'Draft', icon: FileText, color: 'text-gray-600', bg: 'bg-gray-50 border-gray-200' },
  { key: 'calculating', label: 'Calculating', icon: Loader2, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  { key: 'pending_review', label: 'Pending Review', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  { key: 'approved', label: 'Approved', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  { key: 'applied', label: 'Applied to ERP', icon: Zap, color: 'text-purple-600', bg: 'bg-purple-50 border-purple-200' },
  { key: 'failed', label: 'Failed', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
];

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function PlanningPage() {
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<ScenarioStatus | 'all'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { data: scenarios, isLoading, refetch } = usePlanningScenarios();
  const { data: summary } = usePlanningScenarioSummary();
  const calculateMutation = useCalculateScenario();
  const approveMutation = useApproveScenario();
  const rejectMutation = useRejectScenario();
  const createMutation = useCreateScenario();
  const deleteMutation = useDeleteScenario();
  const [deleteTarget, setDeleteTarget] = useState<PlanningScenario | null>(null);

  const filteredScenarios = scenarios?.filter(
    (s) => filterStatus === 'all' || s.status === filterStatus
  ) ?? [];

  const sorted = [...filteredScenarios].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );

  const handleCalculate = async (id: string) => {
    try {
      await calculateMutation.mutateAsync(id);
    } catch (e) {
      console.error('Calculate failed:', e);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await approveMutation.mutateAsync({ id });
    } catch (e) {
      console.error('Approve failed:', e);
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectMutation.mutateAsync({ id });
    } catch (e) {
      console.error('Reject failed:', e);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  const handleCreateScenario = async (payload: CreateScenarioPayload) => {
    try {
      const newScenario = await createMutation.mutateAsync(payload);
      setShowCreateModal(false);
      if (newScenario?.id) {
        navigate(`/planning/${newScenario.id}`);
      }
      // If no id returned, the query invalidation will refresh the list
    } catch (e) {
      console.error('Create scenario failed:', e);
    }
  };

  return (
    <Layout title="AI Production Planner" onRefresh={() => refetch()}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#45bdd6] to-[#674EA7] flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">AI Production Planner</h1>
              <p className="text-sm text-gray-500">
                Planning Scenarios — {summary?.total ?? 0} total
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-gradient-to-r from-[#45bdd6] to-[#674EA7] rounded-lg hover:opacity-90 transition-opacity shadow-sm"
            >
              <Plus className="w-4 h-4" />
              New Scenario
            </button>
            <button
              onClick={() => refetch()}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {statusCards.map((card) => {
            const count = summary?.by_status?.[card.key] ?? 0;
            const isActive = filterStatus === card.key;
            return (
              <button
                key={card.key}
                onClick={() => setFilterStatus(isActive ? 'all' : card.key)}
                className={clsx(
                  'relative flex flex-col items-center p-4 rounded-xl border transition-all',
                  isActive
                    ? 'ring-2 ring-[#45bdd6] shadow-md'
                    : 'hover:shadow-sm',
                  card.bg
                )}
              >
                <card.icon className={clsx('w-5 h-5 mb-1', card.color, card.key === 'calculating' && 'animate-spin')} />
                <span className="text-2xl font-bold text-gray-900">{count}</span>
                <span className="text-xs text-gray-500 mt-0.5">{card.label}</span>
              </button>
            );
          })}
        </div>

        {/* Filter indicator */}
        {filterStatus !== 'all' && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            Filtered by: <span className="font-medium text-gray-700 capitalize">{filterStatus.replace('_', ' ')}</span>
            <button
              onClick={() => setFilterStatus('all')}
              className="text-[#45bdd6] hover:underline"
            >
              Clear
            </button>
          </div>
        )}

        {/* Scenarios Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
              <span className="ml-2 text-gray-500">Loading scenarios...</span>
            </div>
          ) : sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Brain className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-lg font-medium">No scenarios found</p>
              <p className="text-sm mt-1">
                {filterStatus !== 'all'
                  ? 'Try clearing the filter'
                  : 'Click "New Scenario" to create your first planning scenario'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">
                    Scenario
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Status
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Planning Period
                  </th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    <div className="flex items-center justify-center gap-1"><Target className="w-3 h-3" />OTD</div>
                  </th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    <div className="flex items-center justify-center gap-1"><BarChart3 className="w-3 h-3" />Util.</div>
                  </th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Ops
                  </th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Conflicts
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Updated
                  </th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map((scenario) => (
                  <ScenarioRow
                    key={scenario.id}
                    scenario={scenario}
                    onCalculate={handleCalculate}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onDelete={(s) => setDeleteTarget(s)}
                    isCalculating={calculateMutation.isPending}
                    onNavigate={(id) => navigate(`/planning/${id}`)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
        {/* Create Scenario Modal */}
        <CreateScenarioModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateScenario}
          isCreating={createMutation.isPending}
          error={createMutation.error}
        />

        {/* Delete Confirmation Modal */}
        <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Scenario" size="md">
          {deleteTarget && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <Trash2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">
                  <p className="font-medium">Permanently delete this scenario?</p>
                  <p className="mt-1">
                    <strong>{deleteTarget.name}</strong> and all associated data (operations, conflicts, audit trail) will be permanently removed.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </Layout>
  );
}

// ─── AI Instructions Template ────────────────────────────────────────────────

const AI_INSTRUCTIONS_TEMPLATE = `## Priorities
- On-time delivery has highest priority
- Critical customer orders: [enter order numbers here]
- Orders that may be delayed: [enter if applicable]

## Resource Constraints
- Machines out of service: [e.g. WC-03 maintenance 10 Feb–12 Feb]
- Staff unavailable: [e.g. Shift 2 understaffed W7]
- Material shortages: [e.g. raw material X available from 15 Feb only]

## Optimisation Rules
- Minimise setup times: group similar orders
- Capacity utilisation: distribute evenly across all work centres
- Overtime: only if necessary, max 2 h per day
- Weekend work: not permitted

## Special Rules
- [e.g. Order A must be completed before Order B]
- [e.g. Work Centre WC-01 reserved for priority orders only]
- [e.g. Minimum lot size of 50 units]

## Quality / Safety
- [e.g. Product X requires quality inspection after each operation]
- [e.g. Hazardous materials only processed during morning shift]`;

// ─── Create Scenario Modal ───────────────────────────────────────────────────

function CreateScenarioModal({
  isOpen,
  onClose,
  onCreate,
  isCreating,
  error,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (payload: CreateScenarioPayload) => void;
  isCreating: boolean;
  error: Error | null;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [planningStart, setPlanningStart] = useState('');
  const [planningEnd, setPlanningEnd] = useState('');
  const [humanInstructions, setHumanInstructions] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setPlanningStart('');
      setPlanningEnd('');
      setHumanInstructions('');
      setValidationError(null);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Validate required fields
    if (!name.trim()) {
      setValidationError('Scenario name is required.');
      return;
    }
    if (!planningStart) {
      setValidationError('Planning start date is required.');
      return;
    }
    if (!planningEnd) {
      setValidationError('Planning end date is required.');
      return;
    }
    if (new Date(planningEnd) <= new Date(planningStart)) {
      setValidationError('End date must be after start date.');
      return;
    }

    // HTML date input value is already YYYY-MM-DD — send directly
    const payload: CreateScenarioPayload = {
      name: name.trim(),
      planning_start: planningStart,
      planning_end: planningEnd,
    };

    if (description.trim()) payload.description = description.trim();
    if (humanInstructions.trim()) payload.human_instructions = humanInstructions.trim();

    onCreate(payload);
  };

  const apiError = error
    ? (error as { response?: { data?: Record<string, string[]> } })?.response?.data
      ? Object.entries((error as { response?: { data?: Record<string, string[]> } }).response!.data!)
          .map(([key, msgs]) => `${key}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join(' | ')
      : 'Failed to create scenario. Please try again.'
    : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Scenario" size="lg">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Validation / API Errors */}
        {(validationError || apiError) && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{validationError || apiError}</span>
          </div>
        )}

        {/* Scenario Name */}
        <div>
          <label htmlFor="scenario-name" className="block text-sm font-medium text-gray-700 mb-1">
            Scenario Name <span className="text-red-500">*</span>
          </label>
          <input
            id="scenario-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Q1 2026 Production Plan"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6] focus:border-[#45bdd6] outline-none transition-colors"
            autoFocus
          />
        </div>

        {/* Planning Period */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="planning-start" className="block text-sm font-medium text-gray-700 mb-1">
              Planning Start <span className="text-red-500">*</span>
            </label>
            <input
              id="planning-start"
              type="date"
              value={planningStart}
              onChange={(e) => setPlanningStart(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6] focus:border-[#45bdd6] outline-none transition-colors"
            />
          </div>
          <div>
            <label htmlFor="planning-end" className="block text-sm font-medium text-gray-700 mb-1">
              Planning End <span className="text-red-500">*</span>
            </label>
            <input
              id="planning-end"
              type="date"
              value={planningEnd}
              onChange={(e) => setPlanningEnd(e.target.value)}
              min={planningStart || undefined}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6] focus:border-[#45bdd6] outline-none transition-colors"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="scenario-desc" className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            id="scenario-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Brief description of this planning scenario..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6] focus:border-[#45bdd6] outline-none transition-colors resize-none"
          />
        </div>

        {/* AI Instructions */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="scenario-instructions" className="block text-sm font-medium text-gray-700">
              AI Instructions <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            {!humanInstructions.trim() && (
              <button
                type="button"
                onClick={() => setHumanInstructions(AI_INSTRUCTIONS_TEMPLATE)}
                className="flex items-center gap-1 px-2 py-0.5 text-xs text-[#674EA7] bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 transition-colors"
              >
                <FileText className="w-3 h-3" />
                Use Template
              </button>
            )}
          </div>
          <textarea
            id="scenario-instructions"
            value={humanInstructions}
            onChange={(e) => setHumanInstructions(e.target.value)}
            rows={8}
            placeholder="Special instructions for the AI planner..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-[#45bdd6] focus:border-[#45bdd6] outline-none transition-colors resize-y"
          />
          <p className="mt-1 text-xs text-gray-400">
            These instructions are sent directly to the AI between the optimization weights and production data.
            They influence scheduling priorities, constraint handling, and resource allocation.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isCreating}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isCreating}
            className="flex items-center gap-2 px-5 py-2 text-sm text-white bg-gradient-to-r from-[#45bdd6] to-[#674EA7] rounded-lg hover:opacity-90 transition-opacity shadow-sm disabled:opacity-50"
          >
            {isCreating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Create Scenario
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ScenarioRow({
  scenario,
  onCalculate,
  onApprove,
  onReject,
  onDelete,
  isCalculating,
  onNavigate,
}: {
  scenario: PlanningScenario;
  onCalculate: (id: string) => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onDelete: (scenario: PlanningScenario) => void;
  isCalculating: boolean;
  onNavigate: (id: string) => void;
}) {
  const statusInfo = getScenarioStatusInfo(scenario.status);

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      {/* Name & Description */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <Brain className="w-4 h-4 text-[#674EA7] flex-shrink-0" />
          <div>
            <button
              onClick={() => onNavigate(scenario.id)}
              className="font-medium text-gray-900 text-sm hover:text-[#45bdd6] transition-colors text-left"
            >
              {scenario.name}
            </button>
            {scenario.description && (
              <p className="text-xs text-gray-500 truncate max-w-[200px]">{scenario.description}</p>
            )}
          </div>
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-4">
        <span className={clsx('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium', statusInfo.bgColor, statusInfo.color)}>
          {scenario.status === 'calculating' && <Loader2 className="w-3 h-3 animate-spin" />}
          {statusInfo.label}
        </span>
      </td>

      {/* Planning Period */}
      <td className="px-4 py-4 text-sm text-gray-600">
        {formatDate(scenario.planning_start)} — {formatDate(scenario.planning_end)}
      </td>

      {/* On-Time Rate */}
      <td className="px-4 py-4 text-center">
        {scenario.on_time_rate != null ? (
          <span className={clsx('text-sm font-medium', scenario.on_time_rate >= 90 ? 'text-green-600' : scenario.on_time_rate >= 70 ? 'text-amber-600' : 'text-red-600')}>
            {scenario.on_time_rate.toFixed(0)}%
          </span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>

      {/* Utilization */}
      <td className="px-4 py-4 text-center">
        {scenario.capacity_utilization != null ? (
          <span className="text-sm font-medium text-gray-700">{scenario.capacity_utilization.toFixed(0)}%</span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>

      {/* Operations */}
      <td className="px-4 py-4 text-center text-sm text-gray-600">
        {scenario.total_operations || scenario.planned_operation_count || 0}
      </td>

      {/* Conflicts */}
      <td className="px-4 py-4 text-center">
        {(scenario.total_conflicts || scenario.conflict_count || 0) > 0 ? (
          <span className="inline-flex items-center gap-1 text-sm text-red-600 font-medium">
            <AlertTriangle className="w-3.5 h-3.5" />
            {scenario.total_conflicts || scenario.conflict_count}
          </span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>

      {/* Updated */}
      <td className="px-4 py-4 text-xs text-gray-500" title={formatDateTime(scenario.updated_at)}>
        {timeAgo(scenario.updated_at)}
      </td>

      {/* Actions */}
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-1">
          {scenario.status === 'draft' && (
            <button
              onClick={() => onCalculate(scenario.id)}
              disabled={isCalculating}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="AI Calculation starten"
            >
              <Play className="w-4 h-4" />
            </button>
          )}
          {scenario.status === 'pending_review' && (
            <>
              <button
                onClick={() => onApprove(scenario.id)}
                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="Approve"
              >
                <CheckCircle2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onReject(scenario.id)}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Reject"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </>
          )}
          {!['calculating', 'applied'].includes(scenario.status) && (
            <button
              onClick={() => onDelete(scenario)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onNavigate(scenario.id)}
            className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
            title="Details"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}
